/**
 * Next Right Work panel — Doc 17 §1.2
 *
 * Reads work unit readiness from getWorkUnitReadiness (planningQueries.ts)
 * and work unit list from getWorkUnitsForPlanVersion. Shows:
 * - Ready work units (sorted by initiative priority_position)
 * - Blocked work units with named, structured blockers
 * - RECIPE_RESOLVED blocker surfaced as a first-class named state
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Lock, AlertTriangle, Zap, FileCode } from 'lucide-react';
import { getWorkUnitReadiness } from '../../lib/planningQueries';
import { getWorkUnitsForPlanVersion, listInitiativesForPlanVersion, getItemTitlesByIds } from '../../lib/planningService';
import type { WorkUnitReadinessResult } from '../../lib/planningQueries';
import type { WorkUnit, Initiative } from '../../types/planning';

// ─── Types ────────────────────────────────────────────────────────────────────

interface WorkUnitRow {
  wu: WorkUnit;
  initiative: Initiative | undefined;
  readiness: WorkUnitReadinessResult | undefined;
}

// ─── Blocker badge ────────────────────────────────────────────────────────────

const BLOCKER_TYPE_CONFIG: Record<string, { label: string; color: string }> = {
  dependency:              { label: 'Dependency',        color: 'bg-[var(--slate-dim)] text-[var(--text-sec)]' },
  capacity:                { label: 'No capacity',       color: 'bg-[var(--mustard-dim)] text-[var(--mustard)]' },
  capability:              { label: 'Capability gap',    color: 'bg-[var(--slate-dim)] text-[var(--text-sec)]' },
  recipe_pending:          { label: 'Work type needed',    color: 'bg-[var(--mustard-dim)] text-[var(--mustard)]' },
  assumption_invalidated:  { label: 'Assumption invalid',color: 'bg-[var(--coral-dim)] text-[var(--coral)]' },
  structural:              { label: 'Structural',        color: 'bg-[var(--hover-tint)] text-[var(--text-sec)]' },
};

function BlockerBadge({ type, reason }: { type: string; reason: string }) {
  const cfg = BLOCKER_TYPE_CONFIG[type] ?? { label: type, color: 'bg-[var(--hover-tint)] text-[var(--text-sec)]' };
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5  font-medium ${cfg.color}`} title={reason}>
      {type === 'recipe_pending' && <FileCode className="h-3 w-3" />}
      {cfg.label}
    </span>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface NextRightWorkPanelProps {
  planVersionId: string;
  /** The plan: an item it holds shows the title it authorized (1060). */
  planCycleId?: string | null;
  /** Plan base path (e.g. /planning/plans/:cycleId) — rows link into its Work Graph. */
  planBase?: string;
  maxReady?: number;
  maxBlocked?: number;
}

/** Row body renders as a link into the Work Graph when a plan base is known. */
function RowShell({
  href,
  className,
  children,
}: {
  href: string | null;
  className: string;
  children: React.ReactNode;
}) {
  return href ? (
    <Link to={href} className={className}>
      {children}
    </Link>
  ) : (
    <div className={className}>{children}</div>
  );
}

export function NextRightWorkPanel({
  planVersionId,
  planCycleId,
  planBase,
  maxReady = 5,
  maxBlocked = 5,
}: NextRightWorkPanelProps) {
  const [rows, setRows] = useState<WorkUnitRow[]>([]);
  const [ranks, setRanks] = useState<Record<string, number>>({});
  const [itemTitles, setItemTitles] = useState<Record<string, string>>({});
  const [showAllReady, setShowAllReady] = useState(false);
  const [showAllBlocked, setShowAllBlocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    Promise.all([
      getWorkUnitsForPlanVersion(planVersionId),
      listInitiativesForPlanVersion(planVersionId),
      getWorkUnitReadiness(planVersionId),
    ]).then(([wuRes, initRes, readinessRes]) => {
      if (cancelled) return;
      if (wuRes.error) { setError(wuRes.error); setLoading(false); return; }

      const initiatives = initRes.data ?? [];
      const readiness = readinessRes.data ?? [];
      const initById = new Map(initiatives.map((i) => [i.id, i]));
      const readinessById = new Map(readiness.map((r) => [r.work_unit_id, r]));

      const built: WorkUnitRow[] = wuRes.data.map((wu) => ({
        wu,
        initiative: initById.get(wu.initiative_id),
        readiness: readinessById.get(wu.id),
      }));

      // Sort ready ones by initiative priority_position asc (lower = higher priority)
      built.sort((a, b) => {
        const aPos = a.initiative?.priority_position ?? 999;
        const bPos = b.initiative?.priority_position ?? 999;
        return aPos - bPos;
      });

      setRows(built);

      // Rank = the initiative's ordinal in the plan's rendered order (dropped
      // ones are held below the line and never number the funded list). Stored
      // priority_position can be null/duplicated, so the ordinal is the truth.
      const rankMap: Record<string, number> = {};
      initiatives
        .filter((i) => (i.status ?? 'planned') !== 'dropped')
        .forEach((i, idx) => {
          rankMap[i.id] = idx + 1;
        });
      setRanks(rankMap);

      // Look up item titles to override any ID-based fallbacks stored in the DB; an item
      // the plan holds reads the title it authorized (1060).
      const itemIds = [...new Set(initiatives.map((i) => i.item_id).filter(Boolean))];
      if (itemIds.length > 0) {
        void getItemTitlesByIds(itemIds, planCycleId).then(({ data: titleMap }) => {
          if (!cancelled) setItemTitles(titleMap);
        });
      }

      setLoading(false);
    }).catch((e) => {
      if (cancelled) return;
      setError(e instanceof Error ? e.message : 'Failed to load work units');
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [planVersionId, planCycleId]);

  if (loading) {
    return (
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] px-5 py-4">
        <div className="flex items-center gap-2 text-[var(--text-ter)] text-sm">
          <Zap className="h-4 w-4 animate-pulse" /> Loading work units…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] px-5 py-4 text-sm text-[var(--coral)]">
        {error}
      </div>
    );
  }

  const isFinished = (r: WorkUnitRow) =>
    r.initiative?.status === 'complete'
    || r.initiative?.status === 'dropped'
    || r.wu.readiness_state === 'done';

  const hasRecipePending = (r: WorkUnitRow) => {
    const blockers = r.readiness?.structured_blockers ?? [];
    return blockers.some((b) => b.type === 'recipe_pending') || !r.wu.resolved_recipe_id;
  };

  // Only explicit ready; never treat missing readiness / finished work as "ready to start".
  const readyRows = rows.filter(
    (r) => r.readiness?.ready === true && !isFinished(r) && !hasRecipePending(r),
  );
  const blockedRows = rows.filter((r) => {
    if (isFinished(r)) return false;
    if (hasRecipePending(r)) return true;
    return r.readiness?.ready === false && (r.readiness?.blockers?.length ?? 0) > 0;
  });
  const recipePendingCount = rows.filter((r) => !isFinished(r) && hasRecipePending(r)).length;

  if (rows.length === 0) {
    return (
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] px-5 py-4">
        <div className="flex items-center gap-2 mb-3">
          <Zap className="h-4 w-4 text-[var(--text-ter)]" />
          <h2 className="text-sm font-semibold text-[var(--text-pri)]">Next Right Work</h2>
        </div>
        <p className="text-sm text-[var(--text-ter)]">No work units in this plan version yet.</p>
      </div>
    );
  }

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] px-5 py-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-[var(--text-pri)] flex items-center gap-2">
          <Zap className="h-4 w-4 text-[var(--text-ter)]" />
          Next Right Work
        </h2>
        <span className="text-xs text-[var(--text-ter)]">
          {readyRows.length} ready · {blockedRows.length} blocked
        </span>
      </div>

      {/* Recipe pending alert — RECIPE_RESOLVED readiness blocker */}
      {recipePendingCount > 0 && (
        <div className="flex items-start gap-2 px-3 py-2 bg-[var(--mustard-dim)] border border-[var(--mustard)] text-xs text-[var(--mustard)]">
          <FileCode className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div>
            <span className="font-medium">Work type needed</span> — {recipePendingCount} work unit{recipePendingCount !== 1 ? 's' : ''} have no work type to say how the work is approached.
            {/* Sent people to the Work Graph's work-unit detail, which has no
                work-type control at all — capabilities and sequence links, and
                nothing else. The work type is set on the ITEM, on its
                Pre-planning tab, which is where the constraint check's own
                remediation points. Two directions for one fix, one of them a
                dead end. */}
            {' '}Set the work type on the item, under Pre-planning.
          </div>
        </div>
      )}

      {/* Ready work units */}
      {readyRows.length > 0 && (
        <div>
          <p className="text-xs font-medium text-[var(--text-ter)] mb-2 flex items-center gap-1">
            <CheckCircle2 className="h-3.5 w-3.5 text-[var(--green)]" />
            Ready to start
          </p>
          <ul className="space-y-1.5">
            {(showAllReady ? readyRows : readyRows.slice(0, maxReady)).map(({ wu, initiative }) => {
              const initiativeTitle = initiative
                ? (itemTitles[initiative.item_id] || initiative.title || null)
                : null;
              const wuTitle = wu.title?.trim() || null;
              const primary = wuTitle || initiativeTitle || wu.id.slice(0, 8);
              const showInitiativeLine = Boolean(
                initiative && initiativeTitle && initiativeTitle !== primary,
              );
              const rank = initiative ? ranks[initiative.id] : undefined;
              return (
                <li key={wu.id}>
                  <RowShell
                    href={planBase ? `${planBase}/work-graph?workUnitId=${wu.id}` : null}
                    className="flex items-center gap-3 px-3 py-2 bg-[var(--green-dim)]/40 border border-[var(--green)] hover:border-[var(--green)] transition-colors"
                  >
                    <ArrowRight className="h-3.5 w-3.5 text-[var(--green)] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[var(--text-pri)] truncate">{primary}</p>
                      {initiative && (
                        <p className="text-xs text-[var(--text-ter)] truncate">
                          {rank != null ? `Initiative #${rank}` : 'Initiative'}
                          {showInitiativeLine ? ` · ${initiativeTitle}` : ''}
                        </p>
                      )}
                    </div>
                    {wu.estimated_effort_hours != null && wu.estimated_effort_hours > 0 && (
                      <span className="text-xs text-[var(--text-ter)] flex-shrink-0">{wu.estimated_effort_hours}h</span>
                    )}
                  </RowShell>
                </li>
              );
            })}
            {readyRows.length > maxReady && (
              <li>
                <button
                  type="button"
                  onClick={() => setShowAllReady((v) => !v)}
                  className="text-xs font-medium text-primary-700 hover:underline px-3"
                >
                  {showAllReady ? 'Show fewer' : `Show all ${readyRows.length} ready`}
                </button>
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Blocked work units */}
      {blockedRows.length > 0 && (
        <div>
          <p className="text-xs font-medium text-[var(--text-ter)] mb-2 flex items-center gap-1">
            <Lock className="h-3.5 w-3.5 text-[var(--text-ter)]" />
            Blocked
          </p>
          <ul className="space-y-1.5">
            {(showAllBlocked ? blockedRows : blockedRows.slice(0, maxBlocked)).map((row) => {
              const { wu, initiative, readiness } = row;
              const structuredBlockers = readiness?.structured_blockers ?? [];
              const recipePending = hasRecipePending(row);
              const initiativeTitle = initiative
                ? (itemTitles[initiative.item_id] || initiative.title || null)
                : null;
              const primary = wu.title?.trim() || initiativeTitle || wu.id.slice(0, 8);
              const rank = initiative ? ranks[initiative.id] : undefined;
              return (
                <li key={wu.id}>
                  <RowShell
                    href={planBase ? `${planBase}/work-graph?workUnitId=${wu.id}` : null}
                    className="block px-3 py-2 bg-[var(--surface)] border border-[var(--rule)] space-y-1.5 hover:border-[var(--rule)] transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-3.5 w-3.5 text-[var(--mustard)] flex-shrink-0" />
                      <p className="text-sm font-medium text-[var(--text-sec)] truncate flex-1">{primary}</p>
                      {rank != null && (
                        <span className="text-xs text-[var(--text-ter)] flex-shrink-0">#{rank}</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5 pl-5">
                      {recipePending && (
                        <BlockerBadge type="recipe_pending" reason="No work type yet — assign one" />
                      )}
                      {structuredBlockers
                        .filter((b) => b.type !== 'recipe_pending')
                        .slice(0, 3)
                        .map((b, idx) => (
                          <BlockerBadge key={idx} type={b.type} reason={b.reason} />
                        ))}
                    </div>
                  </RowShell>
                </li>
              );
            })}
            {blockedRows.length > maxBlocked && (
              <li>
                <button
                  type="button"
                  onClick={() => setShowAllBlocked((v) => !v)}
                  className="text-xs font-medium text-primary-700 hover:underline px-3"
                >
                  {showAllBlocked ? 'Show fewer' : `Show all ${blockedRows.length} blocked`}
                </button>
              </li>
            )}
          </ul>
        </div>
      )}

      {readyRows.length === 0 && blockedRows.length === 0 && (
        <p className="text-sm text-[var(--text-ter)]">
          Work unit readiness could not be determined — run the planning engine to compute.
        </p>
      )}
    </div>
  );
}
